import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Zap, Trophy, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Sound effects using Web Audio API
const playBuzzSound = (isFirst = false) => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    if (isFirst) {
      // Victory/first place sound - ascending triumphant tones
      const playTone = (freq, startTime, duration) => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = freq;
        oscillator.type = 'sine';
        gainNode.gain.setValueAtTime(0.3, startTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + duration);
        oscillator.start(startTime);
        oscillator.stop(startTime + duration);
      };
      
      const now = audioContext.currentTime;
      playTone(523, now, 0.12);        // C5
      playTone(659, now + 0.12, 0.12); // E5
      playTone(784, now + 0.24, 0.12); // G5
      playTone(1047, now + 0.36, 0.25); // C6 (hold)
    } else {
      // Regular buzz sound - quick beep
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 440;
      oscillator.type = 'square';
      gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.08);
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.08);
    }
  } catch (e) {
    console.log("Audio not supported");
  }
};

const BuzzerButton = ({ sessionId, deviceId, groupNumber, teamBuzzed, buzzPosition, onBuzz }) => {
  const [isPressed, setIsPressed] = useState(false);
  const [isBuzzing, setIsBuzzing] = useState(false);

  const handleBuzz = async () => {
    if (teamBuzzed || isBuzzing) return;
    
    setIsBuzzing(true);
    setIsPressed(true);
    
    try {
      const response = await axios.post(
        `${API}/sessions/${sessionId}/buzz?device_id=${deviceId}`
      );
      
      const position = response.data.position;
      
      // Play sound based on position
      playBuzzSound(position === 1);
      
      if (position === 1) {
        toast.success("🎉 FIRST! Your team buzzed first!");
      } else {
        toast.info(`You're #${position}`);
      }
      
      if (onBuzz) onBuzz(position);
    } catch (error) {
      if (error.response?.data?.detail === "Your team already buzzed!") {
        toast.info("Your team already buzzed!");
      } else if (error.response?.data?.detail === "Buzzer is not active") {
        toast.error("Buzzer is not active yet");
      } else {
        toast.error("Failed to buzz");
      }
    } finally {
      setIsBuzzing(false);
      setTimeout(() => setIsPressed(false), 200);
    }
  };

  // Already buzzed state
  if (teamBuzzed) {
    return (
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="text-center"
      >
        <div className={`w-48 h-48 mx-auto rounded-full flex flex-col items-center justify-center ${
          buzzPosition === 1 
            ? "bg-gradient-to-br from-[#FFD700] to-[#FFA500] shadow-[0_0_60px_rgba(255,215,0,0.5)]" 
            : "bg-gradient-to-br from-[#6B5E7C] to-[#4A4458]"
        }`}>
          {buzzPosition === 1 ? (
            <>
              <Trophy className="w-16 h-16 text-black mb-2" />
              <span className="text-2xl font-bold text-black">1ST!</span>
            </>
          ) : (
            <>
              <span className="text-5xl font-bold text-white">#{buzzPosition}</span>
              <span className="text-sm text-[#E2D9F3] mt-1">Position</span>
            </>
          )}
        </div>
        <p className="text-[#E2D9F3] mt-4">
          {buzzPosition === 1 ? "Your team was first! 🎉" : `Your team is #${buzzPosition}`}
        </p>
      </motion.div>
    );
  }

  // Active buzzer button
  return (
    <motion.div className="text-center">
      <motion.button
        data-testid="buzz-button"
        onClick={handleBuzz}
        disabled={isBuzzing}
        whileTap={{ scale: 0.95 }}
        animate={isPressed ? { scale: 0.95 } : { scale: 1 }}
        className={`
          w-48 h-48 rounded-full 
          bg-gradient-to-br from-[#FF1744] to-[#D50000]
          shadow-[0_8px_30px_rgba(255,23,68,0.4)]
          hover:shadow-[0_8px_40px_rgba(255,23,68,0.6)]
          active:shadow-[0_4px_20px_rgba(255,23,68,0.3)]
          transition-all duration-150
          flex flex-col items-center justify-center
          border-4 border-white/20
          ${isBuzzing ? "opacity-70" : ""}
        `}
      >
        <Zap className="w-16 h-16 text-white mb-1" />
        <span className="text-2xl font-bold text-white tracking-wide">
          {isBuzzing ? "..." : "BUZZ!"}
        </span>
      </motion.button>
      
      <p className="text-[#E2D9F3] mt-4 text-sm">
        Tap to buzz for Team {groupNumber}
      </p>
    </motion.div>
  );
};

// Waiting state when buzzer is not active
const BuzzerWaiting = () => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="text-center"
  >
    <div className="w-48 h-48 mx-auto rounded-full bg-[#1A1625] border-2 border-dashed border-[#6B5E7C] flex flex-col items-center justify-center">
      <Clock className="w-12 h-12 text-[#6B5E7C] mb-2" />
      <span className="text-[#6B5E7C] font-medium">Waiting...</span>
    </div>
    <p className="text-[#6B5E7C] mt-4 text-sm">
      Buzzer will activate when the facilitator starts it
    </p>
  </motion.div>
);

export { BuzzerButton, BuzzerWaiting, playBuzzSound };
